/**
 * Helpers to hand-off state between two chat components (e.g. NewChatInput -> DataCaptureChat)
 *
 * DataCaptureChat shows loading state while the initial message is being processed. It typically causes
 * this by polling the returned status URL.
 *
 * NewChatInput will create the chat and set the status URL. DataCaptureChat will discover this status URL
 * in redux, and trigger a similar behavior that it typically has.
 *
 */

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {ChatFileUpload} from './ChatLibraryTypes';

export interface InitialChatData {
  /* The status URL to poll for the response. */
  statusUrl: Promise<string>;
  /* The initial message to the chat (i.e. client-side shape of a DisplayMessageChatEvent). */
  message: {
    text: string;
    fileUploads: Array<ChatFileUpload>;
    agentType: AgentType;
  };
}

/* Keyed by ChatSession.id */
export const InitialChatDataGlobalClientState = makeUseGlobalClientState<InitialChatData | null>(null);

/**
 * This should be invoked before a chat component is rendered, as chat components may use
 * `getInitialChatData` without a redux subscription.
 *
 * `data` should only transition to-or-from `null`, never between two non-null values, as chat components
 * are not expected to handle this case.
 */
export function setInitialChatData(chatSessionId: string, data: InitialChatData | null): void {
  InitialChatDataGlobalClientState.updateState(chatSessionId, data);
}

/**
 * This is safe to access directly in render, to populate initial state / when chatSessionId changes.
 */
export function getInitialChatData(chatSessionId: string): InitialChatData | null {
  return InitialChatDataGlobalClientState.getState(chatSessionId);
}
